import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardCheck, Plus, X, Search, Trash2, CheckCircle2, Clock, AlertCircle } from "lucide-react";

/**
 * PM UI for assigning courses to learners in the same practice.
 *
 * Props:
 *   learners: [{ id, name, email }]       — from team snapshot
 *   courses:  [{ slug, title }]           — from team snapshot
 *   onChanged: () => void                 — called after a create/delete so parent
 *                                           can refresh the team snapshot
 */
export function AssignmentsPanel({ learners = [], courses = [], onChanged }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/assignments");
      setRows(data.assignments || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not load assignments"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const revoke = async (a) => {
    if (
      !window.confirm(
        `Remove the assignment "${a.course_title}" for ${a.user_name || a.user_email}? Their existing progress is kept.`
      )
    )
      return;
    try {
      await api.delete(`/admin/assignments/${a.id}`);
      toast.success("Assignment removed");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not remove"));
    }
  };

  const grouped = useMemo(() => {
    // Group by learner for readable display
    const map = new Map();
    for (const a of rows || []) {
      const key = a.user_id;
      if (!map.has(key))
        map.set(key, { user_id: a.user_id, user_name: a.user_name, user_email: a.user_email, items: [] });
      map.get(key).items.push(a);
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.user_name || a.user_email || "").localeCompare(b.user_name || b.user_email || "")
    );
  }, [rows]);

  return (
    <div className="mt-8 bg-white border-2 border-brand-secondary/30 rounded-3xl shadow-card overflow-hidden" data-testid="admin-assignments-panel">
      <div className="p-6 border-b border-brand-linen flex flex-wrap items-center gap-3 bg-brand-secondary/5">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.28em] text-brand-secondary font-medium">New · PM tool</div>
          <h2 className="font-serif text-2xl text-brand-ink flex items-center gap-2 mt-1">
            <ClipboardCheck className="w-5 h-5" strokeWidth={1.5} /> Course assignments
          </h2>
          <p className="text-sm text-brand-body mt-1">
            Send named training to specific team members — with an optional due date and a note. They'll see it under "Assigned to you" on their dashboard.
          </p>
        </div>
        <button
          data-testid="admin-assign-course-btn"
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-5 py-2.5 hover:bg-brand-inkHover"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Assign courses
        </button>
      </div>

      <div className="p-6">
        {loading && !rows && <div className="text-sm text-brand-muted">Loading assignments…</div>}
        {rows && rows.length === 0 && (
          <div data-testid="assignments-empty-state" className="rounded-2xl border border-dashed border-brand-linen p-5 bg-brand-bone/40">
            <div className="text-sm text-brand-ink font-medium mb-2">How it works</div>
            <ol className="text-sm text-brand-body space-y-1.5 list-decimal list-inside">
              <li>Click <span className="font-medium text-brand-ink">Assign courses</span> above.</li>
              <li>Tick which team member(s) should do the training.</li>
              <li>Tick which course(s) they should complete.</li>
              <li>Optionally set a due date and a short note (e.g. <em>“Please complete before your appraisal”</em>).</li>
              <li>Hit <span className="font-medium text-brand-ink">Assign</span>. They'll see it on their dashboard immediately — you'll see progress here.</li>
            </ol>
            <div className="text-xs text-brand-muted mt-3 italic">
              Assignments don't restrict access — everyone can still browse any course. This is about pointing your team at the right training and tracking that they've done it.
            </div>
          </div>
        )}
        {grouped.length > 0 && (
          <ul className="space-y-4">
            {grouped.map((g) => (
              <li key={g.user_id} className="border border-brand-linen rounded-2xl p-4">
                <div className="font-medium text-brand-ink">{g.user_name || g.user_email}</div>
                <div className="text-xs text-brand-muted">{g.user_email}</div>
                <ul className="mt-3 space-y-2">
                  {g.items.map((a) => {
                    const due = a.due_at ? new Date(a.due_at) : null;
                    const overdue = due && a.status !== "completed" && due.getTime() < Date.now();
                    const done = a.status === "completed";
                    return (
                      <li
                        key={a.id}
                        data-testid={`assignment-row-${a.id}`}
                        className="flex flex-wrap items-center gap-3 text-sm bg-brand-bone/50 rounded-xl px-3 py-2"
                      >
                        <span className="font-medium text-brand-ink flex-1 min-w-0">{a.course_title}</span>
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-ink">
                            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.7} /> Completed
                          </span>
                        ) : overdue ? (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-accent">
                            <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.7} /> Overdue
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-brand-secondary">
                            <Clock className="w-3.5 h-3.5" strokeWidth={1.7} /> Assigned
                          </span>
                        )}
                        {due && (
                          <span className="text-xs text-brand-muted">
                            Due {due.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        )}
                        {a.notes && <span className="text-xs text-brand-muted italic max-w-[240px] truncate" title={a.notes}>“{a.notes}”</span>}
                        <button
                          data-testid={`assignment-remove-${a.id}`}
                          onClick={() => revoke(a)}
                          className="ml-auto text-xs uppercase tracking-widest text-brand-accent border border-brand-accent/40 rounded-full px-2.5 py-1 hover:bg-brand-accent hover:text-white transition-colors"
                          title="Remove assignment"
                        >
                          <Trash2 className="w-3.5 h-3.5 inline mr-1" strokeWidth={1.7} /> Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showModal && (
        <AssignModal
          learners={learners}
          courses={courses}
          onClose={() => setShowModal(false)}
          onCreated={async () => {
            setShowModal(false);
            await load();
            onChanged && onChanged();
          }}
        />
      )}
    </div>
  );
}

function AssignModal({ learners, courses, onClose, onCreated }) {
  const [userIds, setUserIds] = useState(new Set());
  const [slugs, setSlugs] = useState(new Set());
  const [dueAt, setDueAt] = useState("");
  const [notes, setNotes] = useState("");
  const [learnerSearch, setLearnerSearch] = useState("");
  const [courseSearch, setCourseSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const filteredLearners = useMemo(() => {
    const q = learnerSearch.trim().toLowerCase();
    if (!q) return learners;
    return learners.filter(
      (l) => (l.name || "").toLowerCase().includes(q) || (l.email || "").toLowerCase().includes(q)
    );
  }, [learners, learnerSearch]);

  const filteredCourses = useMemo(() => {
    const q = courseSearch.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((c) => (c.title || "").toLowerCase().includes(q) || (c.slug || "").toLowerCase().includes(q));
  }, [courses, courseSearch]);

  const toggle = (set, setter, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  const canSubmit = userIds.size > 0 && slugs.size > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/assignments", {
        user_ids: Array.from(userIds),
        course_slugs: Array.from(slugs),
        due_at: dueAt || null,
        notes: notes || null,
      });
      toast.success(`Assigned ${data.count} course${data.count === 1 ? "" : "s"}`);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e, "Could not create assignments"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-card max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="assign-modal">
        <div className="p-5 border-b border-brand-linen flex items-center">
          <h3 className="font-serif text-xl text-brand-ink">Assign courses to your team</h3>
          <button
            data-testid="assign-modal-close"
            onClick={onClose}
            className="ml-auto text-brand-muted hover:text-brand-ink"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 grid md:grid-cols-2 gap-6 overflow-y-auto flex-1">
          {/* Learners */}
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-muted mb-2">Learners ({userIds.size} selected)</div>
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                data-testid="assign-modal-learner-search"
                placeholder="Search learners…"
                value={learnerSearch}
                onChange={(e) => setLearnerSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-brand-linen rounded-full bg-brand-bone text-sm focus:outline-none focus:border-brand-ink"
              />
            </div>
            <div className="border border-brand-linen rounded-2xl max-h-72 overflow-y-auto">
              {filteredLearners.length === 0 ? (
                <div className="p-3 text-xs text-brand-muted">No learners.</div>
              ) : (
                filteredLearners.map((l) => (
                  <label
                    key={l.id}
                    className="flex items-center gap-3 px-3 py-2 border-b border-brand-linen last:border-0 cursor-pointer hover:bg-brand-bone/60"
                  >
                    <input
                      type="checkbox"
                      data-testid={`assign-modal-learner-${l.id}`}
                      checked={userIds.has(l.id)}
                      onChange={() => toggle(userIds, setUserIds, l.id)}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-brand-ink truncate">{l.name || "(no name)"}</div>
                      <div className="text-xs text-brand-muted truncate">{l.email}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Courses */}
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-muted mb-2">Courses ({slugs.size} selected)</div>
            <div className="relative mb-2">
              <Search className="w-4 h-4 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
              <input
                data-testid="assign-modal-course-search"
                placeholder="Search courses…"
                value={courseSearch}
                onChange={(e) => setCourseSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-brand-linen rounded-full bg-brand-bone text-sm focus:outline-none focus:border-brand-ink"
              />
            </div>
            <div className="border border-brand-linen rounded-2xl max-h-72 overflow-y-auto">
              {filteredCourses.length === 0 ? (
                <div className="p-3 text-xs text-brand-muted">No courses.</div>
              ) : (
                filteredCourses.map((c) => (
                  <label
                    key={c.slug}
                    className="flex items-center gap-3 px-3 py-2 border-b border-brand-linen last:border-0 cursor-pointer hover:bg-brand-bone/60"
                  >
                    <input
                      type="checkbox"
                      data-testid={`assign-modal-course-${c.slug}`}
                      checked={slugs.has(c.slug)}
                      onChange={() => toggle(slugs, setSlugs, c.slug)}
                    />
                    <div className="text-sm text-brand-ink truncate flex-1">{c.title}</div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Extras */}
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-muted mb-1">Due date (optional)</label>
              <input
                type="date"
                data-testid="assign-modal-due"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                className="w-full px-3 py-2 border border-brand-linen rounded-full bg-brand-bone text-sm focus:outline-none focus:border-brand-ink"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-muted mb-1">Note for the learner (optional)</label>
              <input
                type="text"
                data-testid="assign-modal-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Please complete before your appraisal"
                maxLength={500}
                className="w-full px-3 py-2 border border-brand-linen rounded-full bg-brand-bone text-sm focus:outline-none focus:border-brand-ink"
              />
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-brand-linen flex items-center gap-3">
          <div className="text-xs text-brand-muted">
            {userIds.size} learner{userIds.size === 1 ? "" : "s"} × {slugs.size} course{slugs.size === 1 ? "" : "s"} = {userIds.size * slugs.size} assignment{userIds.size * slugs.size === 1 ? "" : "s"}
          </div>
          <button
            data-testid="assign-modal-cancel"
            onClick={onClose}
            className="ml-auto border border-brand-linen rounded-full px-5 py-2 text-sm hover:bg-brand-bone"
          >
            Cancel
          </button>
          <button
            data-testid="assign-modal-submit"
            onClick={submit}
            disabled={!canSubmit}
            className="bg-brand-ink text-brand-bone rounded-full px-6 py-2 text-sm hover:bg-brand-inkHover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Assigning…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
